import { customerCardsQuery } from "@/api/cardApi";
import { useFragment } from "@/api/generated";
import { CardFragmentFragmentDoc } from "@/api/generated/graphql";
import { useAccounts } from "@/composables/useAccounts";
import { type Card } from "@/composables/useDemoCards";
import useAuthUser from "@/composables/useAuthUser";
import { mapApiCardToUi } from "@/utils/cards";
import type { WatchQueryFetchPolicy } from "@apollo/client/core/watchQueryOptions";
import { isEmpty } from "radash";
import { computed } from "vue";
import { useQuery } from "@vue/apollo-composable";

export function useCards() {
  const { authUser } = useAuthUser();
  const isUserLoggedIn = computed(() => !!authUser.value);

  const { accounts, loading: accountsLoading } = useAccounts({ includeExternal: false });

  const accountNumbers = computed(() => accounts.value?.map(account => account.accountNumber).filter(Boolean) ?? []);

  const queryVariables = computed(() => ({
    accountNumbers: accountNumbers.value.length ? accountNumbers.value : null,
  }));

  const queryOptions = computed(() => ({
    enabled: isUserLoggedIn.value && accountNumbers.value.length > 0,
    fetchPolicy: "cache-and-network" as WatchQueryFetchPolicy,
  }));

  const { result, loading, error, refetch } = useQuery(customerCardsQuery, queryVariables, queryOptions);

  const apiCards = computed(
    () =>
      result.value?.customerCards
        ?.map(card => mapApiCardToUi(useFragment(CardFragmentFragmentDoc, card)))
        .filter((card): card is Card => card !== null) ?? [],
  );

  const cardsLoading = computed(() => loading.value || accountsLoading.value);

  const loadingFirstTime = computed(() => cardsLoading.value && isEmpty(apiCards.value));

  const cards = computed(() => apiCards.value);

  return { cards, accounts, loading: cardsLoading, loadingFirstTime, error, refetch };
}
